import { request } from "./client";

function rangeParams(startDate: string, endDate: string, source = "", playbookId = ""): URLSearchParams {
  const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
  if (source) params.set("source", source);
  if (playbookId) params.set("playbook_id", playbookId);
  return params;
}

export interface TagCount {
  tag: string;
  count: number;
}

export function getTagDistribution(date: string, playbookId = ""): Promise<{ tags: TagCount[] }> {
  const params = new URLSearchParams({ date });
  if (playbookId) params.set("playbook_id", playbookId);
  return request(`/analytics/tag-distribution?${params}`);
}

export interface MetricPercentile {
  p50: number;
  p95: number;
}

export interface TechMetrics {
  turns: number;
  sessions: number;
  metrics: Record<string, MetricPercentile>;
  derived: {
    tts_rtf: MetricPercentile;
    asr_rtf: MetricPercentile;
  };
  cache_hit_rate: number;
  cache_hits: number;
  gpu_total_ms: number;
  session_total_ms: number;
  turns_per_session: number;
}

export function getTechMetrics(startDate: string, endDate: string, source = "", playbookId = ""): Promise<TechMetrics> {
  return request(`/analytics/tech-metrics?${rangeParams(startDate, endDate, source, playbookId)}`);
}

export interface DailyTrendItem {
  date: string;
  total: number;
  answered: number;
  completed: number;
  success: number;
  amd_detected: number;
  no_answer: number;
  busy: number;
  avg_duration_ms: number;
}

export function getDailyTrend(startDate: string, endDate: string, source = "", playbookId = ""): Promise<{ days: DailyTrendItem[] }> {
  return request(`/analytics/daily-trend?${rangeParams(startDate, endDate, source, playbookId)}`);
}

export interface TurnDistributionBin {
  turns: number;
  count: number;
}

export function getTurnDistribution(startDate: string, endDate: string, playbookId: string): Promise<{ bins: TurnDistributionBin[] }> {
  return request(`/analytics/turn-distribution?${rangeParams(startDate, endDate, "", playbookId)}`);
}

export interface HourlyTrendItem {
  hour: string;
  dialed: number;
  answered: number;
}

export function getHourlyTrend(startDate: string, endDate: string, playbookId: string): Promise<{ hours: HourlyTrendItem[] }> {
  return request(`/analytics/hourly-trend?${rangeParams(startDate, endDate, "", playbookId)}`);
}

export interface IntentScoreBucket {
  bucket: number;
  count: number;
}

export function getIntentScoreDistribution(startDate: string, endDate: string, playbookId: string): Promise<{ buckets: IntentScoreBucket[] }> {
  return request(`/analytics/intent-score-distribution?${rangeParams(startDate, endDate, "", playbookId)}`);
}

export interface DirectionCount {
  direction: string;
  count: number;
}

export function getIntentDirectionDistribution(startDate: string, endDate: string, playbookId: string): Promise<{ directions: DirectionCount[] }> {
  return request(`/analytics/intent-direction-distribution?${rangeParams(startDate, endDate, "", playbookId)}`);
}

export interface EvaluationScoreBucket {
  bucket: number;
  count: number;
}

export function getEvaluationScoreDistribution(startDate: string, endDate: string, playbookId: string): Promise<{ buckets: EvaluationScoreBucket[] }> {
  return request(`/analytics/evaluation-score-distribution?${rangeParams(startDate, endDate, "", playbookId)}`);
}

export interface PlaybookDistributionItem {
  playbook_id: string;
  display_name: string;
  total: number;
  answered: number;
}

export function getPlaybookDistribution(startDate: string, endDate: string, source = ""): Promise<{ items: PlaybookDistributionItem[] }> {
  return request(`/analytics/playbook-distribution?${rangeParams(startDate, endDate, source)}`);
}

export interface RejectBreakdownItem {
  playbook_id: string;
  reject_reason: string;
  count: number;
}

export function getRejectBreakdown(startDate: string, endDate: string, source = "", playbookId = ""): Promise<{ items: RejectBreakdownItem[] }> {
  return request(`/analytics/reject-breakdown?${rangeParams(startDate, endDate, source, playbookId)}`);
}
